"use client";

import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from "react-error-boundary";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { logger } from "@/lib/telemetry/logger";

/**
 * Fallback UI shown when an error is caught by the error boundary
 */
function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div className="flex min-h-[400px] items-center justify-center p-6">
      <Card className="max-w-lg w-full border-destructive/50">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">Something went wrong</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            An unexpected error occurred. Please try again or contact support if
            the problem persists.
          </p>
          {process.env.NODE_ENV === "development" && (
            <details className="text-left text-sm bg-muted p-4 rounded-lg">
              <summary className="cursor-pointer font-medium mb-2">
                Error Details (Development Only)
              </summary>
              <pre className="whitespace-pre-wrap text-xs text-destructive overflow-auto max-h-40">
                {error instanceof Error ? (
                  <>
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </>
                ) : (
                  String(error)
                )}
              </pre>
            </details>
          )}
          <div className="flex gap-3 justify-center">
            <Button onClick={resetErrorBoundary} variant="default">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            <Button
              onClick={() => (window.location.href = "/dashboard")}
              variant="outline"
            >
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Error boundary wrapper component for catching React errors
 *
 * Usage:
 * ```tsx
 * <AppErrorBoundary>
 *   <YourComponent />
 * </AppErrorBoundary>
 * ```
 */
export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  const handleError = (error: unknown) => {
    // Log error to telemetry
    const errorObj = error instanceof Error ? error : new Error(String(error));
    logger.error("React Error Boundary caught error", errorObj);
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={() => {
        // Reset any state that might have caused the error
        window.location.reload();
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export { ErrorFallback };
